import { fetchWithAuth } from './api';
import { isElectron } from '../utils/platform';

/**
 * Device context for routing decisions
 */
export interface DeviceContext {
  deviceId: string;
  localIp?: string;
  cloudIp?: string;
  username?: string;
  password?: string;
  port?: number;
}

/**
 * Router configuration
 */
export interface RouterConfig {
  localTimeout?: number; // Default: 500ms
  enableMetrics?: boolean; // Log performance metrics
}

/**
 * Smart router for device operations
 * - Tries local IP first (if available and running in Electron)
 * - Falls back to cloud API on timeout/error
 * - Transparent to caller (same interface as cloud API)
 */
class DeviceRouter {
  private metricsEnabled: boolean = true;

  /**
   * Route a device HTTP request (photo, door open, etc.)
   */
  async route(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    device: DeviceContext,
    body?: any,
    config?: RouterConfig
  ): Promise<any> {
    // 1. Check if local routing is possible
    const canUseLocal = this.canUseLocalRouting(device);

    if (!canUseLocal) {
      // Use cloud API directly
      this.log('info', `Using CLOUD (no local routing available): ${endpoint}`);
      return this.cloudRequest(endpoint, method, device, body);
    }

    // 2. Try local request first
    try {
      this.log('debug', `Trying LOCAL first: ${endpoint}`);
      const localResult = await this.localRequest(endpoint, method, device, body, config);

      if (localResult.success) {
        this.log('success', `LOCAL succeeded: ${endpoint} (${localResult.timeMs}ms)`);
        return localResult.data;
      } else {
        this.log('warn', `LOCAL failed: ${localResult.error}, falling back to CLOUD`);
      }
    } catch (error) {
      this.log('warn', `LOCAL error: ${error}, falling back to CLOUD`);
    }

    // 3. Fallback to cloud API
    this.log('info', `Using CLOUD fallback: ${endpoint}`);
    return this.cloudRequest(endpoint, method, device, body);
  }

  /**
   * Check if local routing is possible
   */
  private canUseLocalRouting(device: DeviceContext): boolean {
    // Must have local IP
    if (!device.localIp) {
      this.log('debug', 'No local IP available');
      return false;
    }

    // Must be running in Electron
    if (!isElectron()) {
      this.log('debug', 'Not running in Electron, using cloud');
      return false;
    }

    // Must have Electron API available
    if (typeof window === 'undefined' || !window.electronAPI?.deviceHttpRequest) {
      this.log('warn', 'Electron API not available');
      return false;
    }

    return true;
  }

  /**
   * Make local HTTP request via Electron IPC
   */
  private async localRequest(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    device: DeviceContext,
    body?: any,
    config?: RouterConfig
  ): Promise<{ success: boolean; data?: any; error?: string; timeMs: number }> {
    const timeout = config?.localTimeout || 500;
    const port = device.port || 80;
    const protocol = port === 443 ? 'https' : 'http';

    // Build local device URL
    const url = `${protocol}://${device.localIp}:${port}${endpoint}`;

    // Call Electron IPC bridge
    return window.electronAPI!.deviceHttpRequest({
      url,
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body,
      timeout,
      digestAuth: device.username && device.password ? {
        username: device.username,
        password: device.password,
      } : undefined,
    });
  }

  /**
   * Make cloud API request via existing fetchWithAuth
   */
  private async cloudRequest(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    device: DeviceContext,
    body?: any
  ): Promise<any> {
    // Build full cloud API URL
    const fullUrl = `/api/v1/iot-connect/facial-recognition/devices/${device.deviceId}${endpoint}`;

    const startTime = Date.now();

    const response = await fetchWithAuth(fullUrl, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });

    const timeMs = Date.now() - startTime;
    this.log('debug', `Cloud request completed: ${timeMs}ms`);

    if (!response.ok) {
      throw new Error(`Cloud API error: ${response.statusText}`);
    }

    // Handle different response types
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return response.json();
    } else if (contentType?.includes('image/')) {
      // For images, return blob as base64
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    }

    return response.text();
  }

  /**
   * Log routing metrics and debug info
   */
  private log(level: 'debug' | 'info' | 'warn' | 'success', message: string) {
    if (!this.metricsEnabled && level === 'debug') return;

    const prefix = '[DeviceRouter]';
    switch (level) {
      case 'debug':
        console.debug(prefix, message);
        break;
      case 'info':
        console.info(prefix, message);
        break;
      case 'warn':
        console.warn(prefix, message);
        break;
      case 'success':
        console.log(prefix, '', message);
        break;
    }
  }
}

// Export singleton
export const deviceRouter = new DeviceRouter();

/**
 * Convenience methods for common device operations
 */
export const deviceOperations = {
  /**
   * Fetch photo from device (photo-proxy)
   */
  async fetchPhoto(
    deviceId: string,
    photoPath: string,
    device: Partial<DeviceContext>
  ): Promise<string> {
    const endpoint = `/photo-proxy?path=${encodeURIComponent(photoPath)}`;
    return deviceRouter.route(endpoint, 'GET', {
      deviceId,
      localIp: device.localIp,
      cloudIp: device.cloudIp,
      username: device.username,
      password: device.password,
      port: device.port,
    } as DeviceContext);
  },

  /**
   * Open door/gate
   */
  async openDoor(
    deviceId: string,
    device: Partial<DeviceContext>,
    gateId: number = 1
  ): Promise<void> {
    const endpoint = '/door/open';
    return deviceRouter.route(endpoint, 'POST', {
      deviceId,
      localIp: device.localIp,
      cloudIp: device.cloudIp,
      username: device.username,
      password: device.password,
      port: device.port,
    } as DeviceContext, { gate_id: gateId });
  },

  /**
   * Test device connection
   */
  async testConnection(
    deviceId: string,
    device: Partial<DeviceContext>
  ): Promise<any> {
    const endpoint = '/test/connection';
    return deviceRouter.route(endpoint, 'GET', {
      deviceId,
      localIp: device.localIp,
      cloudIp: device.cloudIp,
      username: device.username,
      password: device.password,
      port: device.port,
    } as DeviceContext);
  },
};
